import { mapValues } from 'lodash';

import {
    ADD_RATE_CORRECTION_CONDITION,
    CHANGE_CURRENT_TAB_TYPE,
    CHANGE_RATE_CORRECTION_CONDITION,
    CHANGE_RATE_CORRECTION_MASS_RATE_CONTROL,
    CHANGE_RATE_CORRECTION_MODE,
    CHANGE_RATE_CORRECTION_SINGLE_MASS_RATE_CONTROL,
    CLEAR_RATE_CORRECTIONS_EDITOR,
    CLOSE_RATE_CORRECTIONS_EDITOR,
    DELETE_RATE_CORRECTION_CONDITION,
    OPEN_RATE_CORRECTIONS_EDITOR,
    RateCorrectionsEditorAction,
    RESET_RATE_CORRECTIONS_EDITORS,
    TOGGLE_RATE_CORRECTION_AVAILABILITY,
    UPDATE_RATE_CORRECTIONS_EDITOR_VALIDATION
} from '../actions/rateCorrectionsEditor';
import { selectRateCorrectionsEditorByState } from '../selectors/rateCorrections/rateCorrections';
import { getRandomId } from '../service/IdHelpers/IdHelpers';
import {
    applyForWeatherRateCorrectionsItemsIntersectionErrors,
    getDefaultValueWeatherRateCorrectionConditionByType,
    getNextValueForWeatherRateCorrection,
    getRateCorrectionGeneralErrors,
    RateCorrectionValidationParams,
    validateDemographyRateCorrectionItem,
    validateDeviceRateCorrectionItem,
    validateInventoryRateCorrectionItem,
    validateRateCorrectionValue,
    validateRetargetingRateCorrectionItem,
    validateSmartBannersRateCorrectionItem,
    validateTrafficRateCorrectionItem,
    validateVideoAdditionRateCorrectionItem,
    validateWeatherRateCorrectionItem
} from '../service/RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import {
    EditorDemographyRateCorrectionCondition,
    EditorDeviceRateCorrectionCondition,
    EditorInventoryRateCorrectionCondition,
    EditorRateCorrection,
    EditorRateCorrectionCondition,
    EditorRetargetingRateCorrectionCondition,
    EditorSmartBannersRateCorrectionCondition,
    EditorTrafficJamRateCorrectionCondition,
    EditorVideoAdditionRateCorrectionCondition,
    EditorWeatherRateCorrection,
    EditorWeatherRateCorrectionCondition,
    RateControlMode,
    RateCorrectionEditorState,
    RateCorrectionMode,
    RateCorrectionType,
    RatesCorrectionsConstantsData,
    RatesCorrectionsEditorValue,
    WeatherRateCorrectionsType
} from '../typings/ratesCorrections';
import { RateCorrectionEditorsState, State } from '../typings/state';

const defaultMassRateControlValueState = {
    mode: RateControlMode.none
};

const defaultSingleMassRateControlValueState = {
    mode: RateControlMode.up
};

export const defaultCorrectionEditorState: RateCorrectionEditorState = {
    isVisible: false,
    value: {
        [RateCorrectionType.retargeting]: {
            isSwitchedOn: true,
            type: RateCorrectionType.retargeting,
            items: [],
            massRateControlValue: defaultMassRateControlValueState,
            maxConditionsNumber: 100,
            minRate: -100,
            maxRate: 1200,
            hasChanges: false,
            errors: [],
            minTemperatureLimit: -50,
            maxTemperatureLimit: 50
        },
        [RateCorrectionType.device]: {
            isSwitchedOn: true,
            type: RateCorrectionType.device,
            items: [],
            massRateControlValue: defaultMassRateControlValueState,
            minRate: -100,
            maxRate: 1200,
            hasChanges: false,
            errors: [],
            minTemperatureLimit: -50,
            maxTemperatureLimit: 50
        },
        [RateCorrectionType.inventory]: {
            isSwitchedOn: true,
            type: RateCorrectionType.inventory,
            items: [],
            massRateControlValue: defaultMassRateControlValueState,
            minRate: -50,
            maxRate: 1200,
            hasChanges: false,
            errors: [],
            minTemperatureLimit: -50,
            maxTemperatureLimit: 50
        },
        [RateCorrectionType.demography]: {
            isSwitchedOn: true,
            type: RateCorrectionType.demography,
            items: [],
            massRateControlValue: defaultMassRateControlValueState,
            minRate: -100,
            maxRate: 1200,
            maxConditionsNumber: 12,
            hasChanges: false,
            errors: [],
            minTemperatureLimit: -50,
            maxTemperatureLimit: 50
        },
        [RateCorrectionType.videoAddition]: {
            type: RateCorrectionType.videoAddition,
            items: [],
            maxConditionsNumber: 1,
            minRate: -50,
            maxRate: 1200,
            hasChanges: false,
            errors: [],
            minTemperatureLimit: -50,
            maxTemperatureLimit: 50
        },
        [RateCorrectionType.smartBanners]: {
            type: RateCorrectionType.smartBanners,
            items: [],
            maxConditionsNumber: 1,
            minRate: -80,
            maxRate: 1200,
            hasChanges: false,
            errors: [],
            minTemperatureLimit: -50,
            maxTemperatureLimit: 50
        },
        [RateCorrectionType.weather]: {
            type: RateCorrectionType.weather,
            items: [],
            massRateControlValue: defaultMassRateControlValueState,
            singleMassRateControlValue: defaultSingleMassRateControlValueState,
            errorsSingleMassRateControl: [],
            minRate: -100,
            maxRate: 1200,
            maxConditionsNumber: 20,
            hasChanges: false,
            mode: RateCorrectionMode.oneDone,
            errors: [],
            minTemperatureLimit: -50,
            maxTemperatureLimit: 50
        },
        [RateCorrectionType.trafficJam]: {
            type: RateCorrectionType.trafficJam,
            items: [],
            massRateControlValue: defaultMassRateControlValueState,
            minRate: -100,
            maxRate: 1200,
            hasChanges: false,
            errors: []
        }
    }
};

export function rateCorrectionsEditor(
    state: RateCorrectionEditorsState = {},
    action: RateCorrectionsEditorAction,
    fullState: State
): RateCorrectionEditorsState {
    switch (action.type) {
        case OPEN_RATE_CORRECTIONS_EDITOR: {
            const editorState = selectRateCorrectionsEditorByState(state, action.id);
            const correctionsConstants = getRatesCorrectionsConstantsData(action.constants);
            const currentTabType = editorState.currentTabType;

            return {
                ...state,
                [action.id]: {
                    ...editorState,
                    currentTabType,
                    value: mapValues(action.correctionsData, (item: EditorRateCorrection, key: RateCorrectionType) => {
                        return {
                            ...editorState.value[key],
                            ...item,
                            ...correctionsConstants[key]
                        };
                    }),
                    isVisible: true
                }
            };
        }

        case CLOSE_RATE_CORRECTIONS_EDITOR:
            return {
                ...state,
                [action.id]: {
                    ...defaultCorrectionEditorState,
                    ...state[action.id],
                    isVisible: false
                }
            };

        case CLEAR_RATE_CORRECTIONS_EDITOR:
            const newState = { ...state };
            delete newState[action.id];
            return newState;

        case UPDATE_RATE_CORRECTIONS_EDITOR_VALIDATION: {
            const editorState = selectRateCorrectionsEditorByState(state, action.id);
            const tabsWithErrors: Array<RateCorrectionType> = [];
            const validatedEditorValue = mapValues(
                editorState.value,
                (correction: EditorRateCorrection, key: RateCorrectionType) => {
                    const correctionErrorsData = action.errors[correction.type];
                    const items: (EditorRateCorrectionCondition)[] = correction.items;

                    if (!correctionErrorsData.isValid) {
                        tabsWithErrors.push(key);
                    }

                    const baseChanges = {
                        items: items.map(item => {
                            return {
                                ...item,
                                errors: correctionErrorsData.items[item.id]
                            };
                        }),
                        errors: correctionErrorsData.general
                    };

                    const changesCorrection = {
                        ...baseChanges,
                        errorsSingleMassRateControl: correctionErrorsData.errorsSingleMassRateControl
                    };

                    return {
                        ...correction,
                        ...changesCorrection
                    };
                }
            );

            const hasErrors = tabsWithErrors.length !== 0;

            return {
                ...state,
                [action.id]: {
                    ...editorState,
                    value: validatedEditorValue as RatesCorrectionsEditorValue,
                    isVisible: hasErrors,
                    currentTabType: hasErrors ? tabsWithErrors[0] : editorState.currentTabType
                }
            };
        }

        case CHANGE_CURRENT_TAB_TYPE: {
            const editorState = selectRateCorrectionsEditorByState(state, action.id);

            return {
                ...state,
                [action.id]: {
                    ...editorState,
                    currentTabType: action.tabType
                }
            };
        }

        case TOGGLE_RATE_CORRECTION_AVAILABILITY: {
            const editorState = selectRateCorrectionsEditorByState(state, action.id);

            return {
                ...state,
                [action.id]: {
                    ...editorState,
                    value: {
                        ...editorState.value,
                        [action.tabType]: {
                            ...editorState.value[action.tabType],
                            hasChanges: true,
                            isSwitchedOn: !editorState.value[action.tabType].isSwitchedOn
                        }
                    }
                }
            };
        }

        case CHANGE_RATE_CORRECTION_MASS_RATE_CONTROL: {
            const editorState = selectRateCorrectionsEditorByState(state, action.id);

            const {
                tabType,
                value: { value, mode },
                id
            } = action;
            const rateCorrectionEditor = editorState.value[tabType];
            const { massRateControlValue } = rateCorrectionEditor;
            const items: EditorRateCorrectionCondition[] = rateCorrectionEditor.items;

            if (massRateControlValue === undefined) {
                return state;
            }

            const isNotEmptyMode = mode !== RateControlMode.none;
            const isNotEmptyValue = value !== undefined;
            const validationParams = {
                maxRate: rateCorrectionEditor.maxRate,
                minRate: rateCorrectionEditor.minRate
            };
            const newItems = items.map(
                (item: EditorRateCorrectionCondition): EditorRateCorrectionCondition => {
                    const newValue = {
                        value: isNotEmptyValue ? value : item.value.value,
                        mode: isNotEmptyMode ? mode : item.value.mode
                    };

                    const newItem = {
                        ...item,
                        value: newValue
                    };

                    if (item.value.value) {
                        newItem.errors.value = validateRateCorrectionValue(newValue, validationParams);
                    }

                    return newItem;
                }
            );
            const newEditorValue = {
                ...rateCorrectionEditor,
                hasChanges: true,
                massRateControlValue: {
                    mode,
                    value
                },
                items: newItems
            };

            return {
                ...state,
                [id]: {
                    ...editorState,
                    value: {
                        ...editorState.value,
                        [tabType]: {
                            ...newEditorValue,
                            errors: getRateCorrectionGeneralErrors(newEditorValue as EditorRateCorrection)
                        }
                    }
                }
            };
        }

        case CHANGE_RATE_CORRECTION_SINGLE_MASS_RATE_CONTROL: {
            const editorState = selectRateCorrectionsEditorByState(state, action.id);

            const { tabType, value, id } = action;

            const validationParams = {
                maxRate: editorState.value[tabType].maxRate,
                minRate: editorState.value[tabType].minRate
            };

            const newValue = {
                ...editorState.value[tabType],
                hasChanges: true,
                singleMassRateControlValue: {
                    ...value
                },
                errorsSingleMassRateControl: validateRateCorrectionValue(action.value, validationParams)
            };

            return {
                ...state,
                [id]: {
                    ...editorState,
                    value: {
                        ...editorState.value,
                        [editorState.value[tabType].type]: {
                            ...newValue,
                            errors: getRateCorrectionGeneralErrors(newValue)
                        }
                    }
                }
            };
        }

        case DELETE_RATE_CORRECTION_CONDITION: {
            const editorState = selectRateCorrectionsEditorByState(state, action.id);
            const { tabType, id } = action;
            const items: (EditorRateCorrectionCondition)[] = editorState.value[tabType].items;
            let newItems: (EditorRateCorrectionCondition)[] = items.filter(item => item.id !== action.conditionId);
            const { mode } = editorState.value[tabType] as EditorWeatherRateCorrection;

            switch (tabType) {
                case RateCorrectionType.weather:
                    newItems = applyForWeatherRateCorrectionsItemsIntersectionErrors(
                        newItems as EditorWeatherRateCorrectionCondition[],
                        mode
                    );

                    break;
                case RateCorrectionType.videoAddition:
                case RateCorrectionType.demography:
                case RateCorrectionType.device:
                case RateCorrectionType.retargeting:
                case RateCorrectionType.inventory:
                case RateCorrectionType.smartBanners:
                case RateCorrectionType.trafficJam:
                    break;
                default:
                    throw new Error('Неподдерживаемый тип корректировок ставок');
            }

            const newCorrectionData = {
                ...editorState.value[tabType],
                hasChanges: true,
                items: newItems,
                massRateControlValue:
                    newItems.length === 1
                        ? {
                              mode: RateControlMode.none
                          }
                        : editorState.value[tabType].massRateControlValue
            };

            return {
                ...state,
                [id]: {
                    ...editorState,
                    value: {
                        ...editorState.value,
                        [tabType]: {
                            ...newCorrectionData,
                            errors: getRateCorrectionGeneralErrors(newCorrectionData as EditorRateCorrection)
                        }
                    }
                }
            };
        }

        case ADD_RATE_CORRECTION_CONDITION: {
            const editorState = selectRateCorrectionsEditorByState(state, action.id);
            const items: (EditorRateCorrectionCondition)[] = editorState.value[action.tabType].items;
            const defaultValue = { mode: RateControlMode.up };
            let newItems;

            switch (action.tabType) {
                case RateCorrectionType.retargeting:
                    newItems = items.concat(
                        action.conditionIds.map(retargetingConditionId => {
                            return {
                                id: getRandomId(),
                                retargetingConditionId,
                                value: { ...defaultValue },
                                errors: {}
                            };
                        })
                    );

                    break;

                case RateCorrectionType.device:
                    newItems = items.concat(
                        action.conditionIds.map(deviceType => {
                            return {
                                id: getRandomId(),
                                deviceType: deviceType,
                                value: { ...defaultValue },
                                errors: {}
                            };
                        })
                    );

                    break;

                case RateCorrectionType.inventory:
                    newItems = items.concat(
                        action.conditionIds.map(inventoryType => {
                            return {
                                id: getRandomId(),
                                inventoryType,
                                value: { ...defaultValue },
                                errors: {}
                            };
                        })
                    );

                    break;

                case RateCorrectionType.videoAddition:
                case RateCorrectionType.demography:
                case RateCorrectionType.smartBanners:
                    newItems = items.concat([
                        {
                            id: getRandomId(),
                            value: { ...defaultValue },
                            errors: {}
                        }
                    ]);

                    break;

                case RateCorrectionType.weather:
                    const { mode, minTemperatureLimit, maxTemperatureLimit } = editorState.value[
                        action.tabType
                    ] as EditorWeatherRateCorrection;

                    newItems = items.concat(
                        action.conditionIds.map(item =>
                            getDefaultValueWeatherRateCorrectionConditionByType(item as WeatherRateCorrectionsType, {
                                minTemperature: minTemperatureLimit,
                                maxTemperature: maxTemperatureLimit
                            })
                        )
                    );

                    newItems = applyForWeatherRateCorrectionsItemsIntersectionErrors(
                        newItems as EditorWeatherRateCorrectionCondition[],
                        mode
                    );

                    break;

                case RateCorrectionType.trafficJam:
                    newItems = items.concat(
                        action.conditionIds.map(trafficJamType => {
                            return {
                                id: getRandomId(),
                                trafficJamType: trafficJamType,
                                value: { ...defaultValue },
                                errors: {}
                            };
                        })
                    );

                    break;

                default:
                    throw Error('Неподдерживаемый для добавления тип корректировки ставки');
            }

            return {
                ...state,
                [action.id]: {
                    ...editorState,
                    value: {
                        ...editorState.value,
                        [action.tabType]: {
                            ...editorState.value[action.tabType],
                            items: newItems,
                            hasChanges: false,
                            massRateControlValue:
                                editorState.value[action.tabType].massRateControlValue === undefined
                                    ? undefined
                                    : { mode: RateControlMode.none }
                        }
                    }
                }
            };
        }

        case CHANGE_RATE_CORRECTION_CONDITION: {
            const editorState = selectRateCorrectionsEditorByState(state, action.id);
            const { id, type } = action.params;
            const validationParams = {
                maxRate: editorState.value[type].maxRate,
                minRate: editorState.value[type].minRate
            };

            let newValue;

            switch (action.params.type) {
                case RateCorrectionType.retargeting:
                    newValue = {
                        ...editorState.value[type],
                        hasChanges: true,
                        items: changeRetargetingCorrectionCondition(
                            id,
                            action.params.value,
                            editorState.value[type].items as EditorRetargetingRateCorrectionCondition[],
                            validationParams
                        )
                    };

                    break;

                case RateCorrectionType.device:
                    newValue = {
                        ...editorState.value[type],
                        hasChanges: true,
                        items: changeDeviceCorrectionCondition(
                            id,
                            action.params.value,
                            editorState.value[type].items as EditorDeviceRateCorrectionCondition[],
                            validationParams
                        )
                    };

                    break;

                case RateCorrectionType.inventory:
                    newValue = {
                        ...editorState.value[type],
                        hasChanges: true,
                        items: changeInventoryCorrectionCondition(
                            id,
                            action.params.value,
                            editorState.value[type].items as EditorInventoryRateCorrectionCondition[],
                            validationParams
                        )
                    };

                    break;

                case RateCorrectionType.demography:
                    newValue = {
                        ...editorState.value[type],
                        hasChanges: true,
                        items: changeDemographyCorrectionCondition(
                            id,
                            action.params.value,
                            editorState.value[type].items,
                            validationParams
                        )
                    };

                    break;

                case RateCorrectionType.videoAddition:
                    newValue = {
                        type: RateCorrectionType.videoAddition,
                        ...editorState.value[type],
                        hasChanges: true,
                        items: changeVideoAdditionCorrectionCondition(
                            id,
                            action.params.value,
                            editorState.value[type].items,
                            validationParams
                        )
                    };

                    break;

                case RateCorrectionType.weather:
                    const { items: currentItems, mode, minTemperatureLimit, maxTemperatureLimit } = editorState.value[
                        type
                    ] as EditorWeatherRateCorrection;

                    const newItems = changeWeatherCorrectionCondition(id, action.params.value, currentItems, {
                        ...validationParams,
                        mode: mode,
                        minTemperature: minTemperatureLimit,
                        maxTemperature: maxTemperatureLimit
                    });

                    newValue = {
                        ...editorState.value[type],
                        hasChanges: true,
                        items: applyForWeatherRateCorrectionsItemsIntersectionErrors(newItems, mode)
                    };

                    break;

                case RateCorrectionType.smartBanners:
                    newValue = {
                        type: RateCorrectionType.smartBanners,
                        ...editorState.value[type],
                        hasChanges: true,
                        items: changeSmartBannersCorrectionCondition(
                            id,
                            action.params.value,
                            editorState.value[type].items,
                            validationParams
                        )
                    };

                    break;

                case RateCorrectionType.trafficJam:
                    newValue = {
                        ...editorState.value[type],
                        hasChanges: true,
                        items: changeTrafficCorrectionCondition(
                            id,
                            action.params.value,
                            editorState.value[type].items as EditorTrafficJamRateCorrectionCondition[],
                            validationParams
                        )
                    };

                    break;

                default:
                    throw Error('Неподдерживаемый тип корректировки ставки');
            }

            return {
                ...state,
                [action.id]: {
                    ...editorState,
                    value: {
                        ...editorState.value,
                        [type]: {
                            ...newValue,
                            errors: getRateCorrectionGeneralErrors(newValue as EditorRateCorrection)
                        }
                    }
                }
            };
        }

        case CHANGE_RATE_CORRECTION_MODE:
            const editor = state[action.groupId];

            let newItemsList: EditorRateCorrectionCondition[];

            switch (editor.value[action.tabType].type) {
                case RateCorrectionType.weather:
                    newItemsList = applyForWeatherRateCorrectionsItemsIntersectionErrors(
                        editor.value[action.tabType].items as EditorWeatherRateCorrectionCondition[],
                        action.value
                    );

                    break;
                case RateCorrectionType.videoAddition:
                case RateCorrectionType.demography:
                case RateCorrectionType.device:
                case RateCorrectionType.retargeting:
                case RateCorrectionType.inventory:
                case RateCorrectionType.smartBanners:
                    newItemsList = [...editor.value[action.tabType].items];

                    break;
                default:
                    throw new Error('Неподдерживаемый тип корректировок ставок');
            }

            return {
                ...state,
                [action.groupId]: {
                    ...editor,
                    value: {
                        ...editor.value,
                        [action.tabType]: {
                            ...editor.value[action.tabType],
                            items: newItemsList,
                            mode: action.value
                        }
                    }
                }
            };

        case RESET_RATE_CORRECTIONS_EDITORS: {
            return {};
        }

        default:
            return state;
    }
}

const changeRetargetingCorrectionCondition = (
    id: string,
    value: EditorRetargetingRateCorrectionCondition,
    currentItems: EditorRetargetingRateCorrectionCondition[],
    validationParams: RateCorrectionValidationParams
): EditorRetargetingRateCorrectionCondition[] => {
    return currentItems.map((item: EditorRetargetingRateCorrectionCondition) => {
        if (item.id !== id) {
            return item;
        }

        const newItemValue = {
            ...item,
            ...value
        };

        const validationResult = validateRetargetingRateCorrectionItem(newItemValue, validationParams);

        return {
            ...newItemValue,
            errors: {
                value: newItemValue.value !== item.value ? validationResult.value : item.errors.value
            }
        };
    });
};

const changeDeviceCorrectionCondition = (
    id: string,
    value: EditorDeviceRateCorrectionCondition,
    currentItems: EditorDeviceRateCorrectionCondition[],
    validationParams: RateCorrectionValidationParams
): EditorDeviceRateCorrectionCondition[] => {
    return currentItems.map((item: EditorDeviceRateCorrectionCondition) => {
        if (item.id !== id) {
            return item;
        }

        const newItemValue = {
            ...item,
            ...value
        };

        const validationResult = validateDeviceRateCorrectionItem(newItemValue, validationParams);

        return {
            ...newItemValue,
            errors: {
                value: newItemValue.value !== item.value ? validationResult.value : item.errors.value
            }
        };
    });
};

const changeTrafficCorrectionCondition = (
    id: string,
    value: EditorTrafficJamRateCorrectionCondition,
    currentItems: EditorTrafficJamRateCorrectionCondition[],
    validationParams: RateCorrectionValidationParams
): EditorTrafficJamRateCorrectionCondition[] => {
    return currentItems.map((item: EditorTrafficJamRateCorrectionCondition) => {
        if (item.id !== id) {
            return item;
        }

        const newItemValue = {
            ...item,
            ...value
        };

        const validationResult = validateTrafficRateCorrectionItem(newItemValue, validationParams);

        return {
            ...newItemValue,
            errors: {
                value: newItemValue.value !== item.value ? validationResult.value : item.errors.value
            }
        };
    });
};

const changeInventoryCorrectionCondition = (
    id: string,
    value: EditorInventoryRateCorrectionCondition,
    currentItems: EditorInventoryRateCorrectionCondition[],
    validationParams: RateCorrectionValidationParams
): EditorInventoryRateCorrectionCondition[] => {
    return currentItems.map((item: EditorInventoryRateCorrectionCondition) => {
        if (item.id !== id) {
            return item;
        }

        const newItemValue = {
            ...item,
            ...value
        };

        const validationResult = validateInventoryRateCorrectionItem(newItemValue, validationParams);

        return {
            ...newItemValue,
            errors: {
                value: newItemValue.value !== item.value ? validationResult.value : item.errors.value
            }
        };
    });
};

const changeDemographyCorrectionCondition = (
    id: string,
    value: EditorDemographyRateCorrectionCondition,
    currentItems: EditorDemographyRateCorrectionCondition[],
    validationParams: RateCorrectionValidationParams
): EditorDemographyRateCorrectionCondition[] => {
    return currentItems.map((item: EditorDemographyRateCorrectionCondition) => {
        if (item.id !== id) {
            return item;
        }

        const newItemValue = {
            ...item,
            ...value
        };

        const validationResult = validateDemographyRateCorrectionItem(newItemValue, validationParams);

        return {
            ...newItemValue,
            errors: {
                value: newItemValue.value !== item.value ? validationResult.value : item.errors.value,
                age: newItemValue.age !== item.age ? validationResult.age : item.errors.age,
                gender: newItemValue.gender !== item.gender ? validationResult.gender : item.errors.gender
            }
        };
    });
};

const changeVideoAdditionCorrectionCondition = (
    id: string,
    value: EditorVideoAdditionRateCorrectionCondition,
    currentItems: EditorVideoAdditionRateCorrectionCondition[],
    validationParams: RateCorrectionValidationParams
): EditorVideoAdditionRateCorrectionCondition[] => {
    return currentItems.map((item: EditorVideoAdditionRateCorrectionCondition) => {
        if (item.id !== id) {
            return item;
        }

        const newItemValue = {
            ...item,
            ...value
        };

        const validationResult = validateVideoAdditionRateCorrectionItem(newItemValue, validationParams);

        return {
            ...newItemValue,
            errors: {
                value: newItemValue.value !== item.value ? validationResult.value : item.errors.value
            }
        };
    });
};

const changeSmartBannersCorrectionCondition = (
    id: string,
    value: EditorSmartBannersRateCorrectionCondition,
    currentItems: EditorSmartBannersRateCorrectionCondition[],
    validationParams: RateCorrectionValidationParams
): EditorSmartBannersRateCorrectionCondition[] => {
    return currentItems.map((item: EditorSmartBannersRateCorrectionCondition) => {
        if (item.id !== id) {
            return item;
        }

        const newItemValue = {
            ...item,
            ...value
        };

        const validationResult = validateSmartBannersRateCorrectionItem(newItemValue, validationParams);

        return {
            ...newItemValue,
            errors: {
                value: newItemValue.value !== item.value ? validationResult.value : item.errors.value
            }
        };
    });
};

const changeWeatherCorrectionCondition = (
    id: string,
    value: EditorWeatherRateCorrectionCondition,
    currentItems: EditorWeatherRateCorrectionCondition[],
    validationParams: RateCorrectionValidationParams
): EditorWeatherRateCorrectionCondition[] => {
    return currentItems.map((item: EditorWeatherRateCorrectionCondition) => {
        if (item.id !== id) {
            return item;
        }

        const unionItem = {
            ...item,
            ...value
        };

        const newItemValue = getNextValueForWeatherRateCorrection(unionItem, validationParams);

        const validationResult = validateWeatherRateCorrectionItem(newItemValue, validationParams);

        return {
            ...newItemValue,
            errors: {
                value: newItemValue.value !== item.value ? validationResult.value : item.errors.value,
                maxTemperature: validationResult.maxTemperature
            }
        };
    });
};

const getRatesCorrectionsConstantsData = (
    rateCorrectionsConstants: RatesCorrectionsConstantsData
):
    | Record<
          RateCorrectionType,
          {
              maxConditionsNumber: number;
              minRate: number;
              maxRate: number;
          }
      >
    | {} => {
    return Object.keys(rateCorrectionsConstants).reduce((acc, type) => {
        acc[type] = {
            minRate: rateCorrectionsConstants[type].percentRange.min,
            maxRate: rateCorrectionsConstants[type].percentRange.max
        };

        if (type !== RateCorrectionType.device) {
            acc[type] = {
                ...acc[type],
                maxConditionsNumber: rateCorrectionsConstants[type].maxConditions
            };
        }

        return acc;
    }, {});
};
